// 261005 E1-P1 — gọi `GET /adaptation/workbench/:storyId`; poll 5s khi còn job chạy; báo khi job vừa hết.
import { useCallback, useEffect, useRef, useState } from 'react';
import { adaptationApi } from '../../../services/api/adaptation';
import type { WorkbenchView } from '../../../types/adaptationWorkbench';
import { isWorkbenchBusy, shouldFireFinished, shouldPollWorkbench } from './appendRunView';
import { EndedJob, endedJobs } from './jobTracking';

export const WORKBENCH_POLL_MS = 5000;

export interface UseWorkbench {
  workbench: WorkbenchView | null;
  /** Lỗi tải tóm tắt (khung vẫn hiện). */
  error: boolean;
  refresh: () => Promise<void>;
}

/** `onJobsFinished` gọi MỘT lần khi workbench đi từ bận (job chạy HOẶC chuỗi `appendRun` RUNNING) về rảnh (nơi gọi nạp lại dữ liệu chặng). */
export const useWorkbench = (storyId: string, onJobsFinished?: () => void, onJobsEnded?: (jobs: EndedJob[]) => void): UseWorkbench => {
  const [workbench, setWorkbench] = useState<WorkbenchView | null>(null);
  const [error, setError] = useState(false);
  const currentStory = useRef(storyId);
  currentStory.current = storyId;
  const prevBusy = useRef<boolean | null>(null);
  const finishedRef = useRef(onJobsFinished);
  finishedRef.current = onJobsFinished;
  const endedRef = useRef(onJobsEnded);
  endedRef.current = onJobsEnded;
  const prevRunning = useRef<WorkbenchView['jobs']['running'] | null>(null);

  const refresh = useCallback(async () => {
    const asked = storyId;
    try {
      const w = await adaptationApi.workbench(storyId);
      if (currentStory.current !== asked) return;
      const drop = shouldFireFinished(prevBusy.current, w);
      prevBusy.current = isWorkbenchBusy(w);
      const ended = endedJobs(prevRunning.current, w.jobs);
      prevRunning.current = w.jobs.running;
      setWorkbench(w);
      setError(false);
      if (drop) finishedRef.current?.();
      if (ended.length) endedRef.current?.(ended);
    } catch {
      if (currentStory.current === asked) setError(true);
    }
  }, [storyId]);

  useEffect(() => {
    setWorkbench(null); setError(false); prevBusy.current = null; prevRunning.current = null;
    refresh();
  }, [refresh]);

  const polling = !!workbench && shouldPollWorkbench(workbench);
  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(() => { refresh(); }, WORKBENCH_POLL_MS);
    return () => clearInterval(t);
  }, [polling, refresh]);

  return { workbench, error, refresh };
};
